import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import {
  SG_FETCH_DEBRIEF_SCHEDULE,
  SG_GET_ORGANIZATION_SELECT,
  SG_GET_CHAT_GPT,
} from "constants/actions";

// shadcn/ui components
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "components/ui/card";
import { Badge } from "components/ui/badge";
import { Separator } from "components/ui/separator";
import { Alert, AlertDescription } from "components/ui/alert";
import { cn } from "lib/utils";

// Lucide icons
import {
  ArrowLeft,
  ExternalLink,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Calendar,
  Users,
  FileText,
  Building2,
  Clock,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

import { ErrorWrapperCustom } from "error/component_error";
import AdditionalType from "./AdditionalType";
import EmpList from "./EmpList";
import ExtendSurvey from "./ExtendSurvey";
import MlFeedback from "./MlFeedback";
import BasicErrorMessage from "utilities/ErrorMessages";
import SurveySummary from "./SurveySummary";
import FixAiResponse from "./FixAiResponse";
import TokenList from "briefToken/List";
import ResponseList from "briefResponse/List";
import AiSummary from "./AiSummary";
import CategorySummary from "./CategorySummary";
import MemberList from "./MemberList";
import EditScheduleDialog from "./EditScheduleDialog";

const Details = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [deBriefId, setDebriefId] = useState(null);
  const [debriefDetails, setDebriefDetails] = useState(null);
  const [empUpdateRow, setEmpUpdateRow] = useState(true);
  const [searchTerm, setSearchTerm] = useState(false);
  const [isAiHidden, setIsAiHidden] = useState(true);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  function Back() {
    navigate(-1);
  }
  useEffect(() => {
    setDebriefId(id);
  }, [id]);

  useEffect(() => {
    // dispatch({
    //   type: SG_GET_SURVEY_QUESTION_SELECT,
    // });
    dispatch({
      type: SG_GET_ORGANIZATION_SELECT,
    });
  }, [dispatch]);

  useEffect(() => {
    if (deBriefId) {
      dispatch({
        type: SG_FETCH_DEBRIEF_SCHEDULE,
        payload: { id: deBriefId },
      });
      dispatch({
        type: SG_GET_CHAT_GPT,
        payload: `debrief-schedule-id=${deBriefId}`,
      });
    }
  }, [dispatch, deBriefId]);
  //

  const { get_brief_schedules, get_organization_select, getchat_gpt } =
    useSelector(
      (state) => ({
        get_brief_schedules: state.debrief_schedule,
        get_organization_select: state.organizations.organization_select,
        getchat_gpt: state.chat_gpt,
      }),
      shallowEqual
    );

  useEffect(() => {
    setSearchTerm(get_brief_schedules.search_term);
  }, [get_brief_schedules]);

  useEffect(() => {
    setDebriefDetails(get_brief_schedules?.[deBriefId]);
  }, [get_brief_schedules, deBriefId]);

  function handleAiToggle() {
    setIsAiHidden(!isAiHidden);
  }

  return (
    <div className="space-y-6 px-6 pt-6 pb-8">
      <BasicErrorMessage />
      
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={Back}
            className="h-9 w-9"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Details of {debriefDetails?.type_of_text}{" "}
              {debriefDetails?.survey_type_text}
            </h1>
          </div>
        </div>
        {debriefDetails?.id && (
          <Button
            onClick={() => setIsEditDialogOpen(true)}
            variant="outline"
            className="gap-2"
          >
            <FileText className="h-4 w-4" />
            Edit Schedule
          </Button>
        )}
      </div>

      {/* Action Sections */}
      {debriefDetails?.status > 3 && (
        <div className="space-y-4">
          <ExtendSurvey debriefDetails={debriefDetails} />
          <MlFeedback debriefDetails={debriefDetails} />
        </div>
      )}

      <FixAiResponse deBriefId={deBriefId} />
      <CategorySummary debriefDetails={debriefDetails} />
      
      {/* Deep Dive Analytics Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            <CardTitle>Deep Dive Analytics</CardTitle>
          </div>
          <CardDescription>
            Comprehensive analysis with survey structure, response analytics, and category breakdowns
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={() => navigate(`/app/brief-schedule/details/${deBriefId}/deep-dive-report`)}
            className="mt-2"
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            View Deep Dive Report
          </Button>
        </CardContent>
      </Card>
      
      <SurveySummary debriefDetails={debriefDetails} />

      {/* Main Details Card */}
      <Card key={debriefDetails?.id}>
        <CardHeader>
          <CardTitle>Survey Details</CardTitle>
          <CardDescription>Complete information about this survey schedule</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Basic Information Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-1">
              <div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Organization ID
              </div>
              <div className="text-base font-semibold">{debriefDetails?.organization}</div>
            </div>
            
            <div className="space-y-1">
              <div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Organization
              </div>
              <div className="text-base font-semibold">
                {debriefDetails?.organization &&
                  get_organization_select.filter(
                    (org) => org.id === debriefDetails?.organization
                  )?.[0]?.name}
              </div>
            </div>

            <div className="space-y-1">
              <div className="text-sm font-medium text-muted-foreground">Type</div>
              <Badge variant="secondary" className="text-sm">
                {debriefDetails?.type_of_text}
              </Badge>
            </div>
            
            <div className="space-y-1">
              <div className="text-sm font-medium text-muted-foreground">Status</div>
              <Badge variant="default" className="text-sm">
                {debriefDetails?.status_text}
              </Badge>
            </div>
            
            <div className="space-y-1">
              <div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Time Zone
              </div>
              <div className="text-base">{debriefDetails?.time_zone}</div>
            </div>
            
            <div className="space-y-1">
              <div className="text-sm font-medium text-muted-foreground">Question</div>
              <div className="flex items-center gap-2">
                <span className="text-base">
                  {debriefDetails?.brief_question?.name} (
                  {debriefDetails?.brief_question?.sort_order}){" "}
                  {debriefDetails?.brief_question?.status_text}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() =>
                    navigate(
                      `/app/questions/details/${debriefDetails?.brief_question?.id}`
                    )
                  }
                >
                  <ExternalLink className="h-4 w-4 text-primary" />
                </Button>
              </div>
            </div>
            
            <div className="space-y-1">
              <div className="text-sm font-medium text-muted-foreground">Name</div>
              <div className="text-base font-semibold">{debriefDetails?.name}</div>
            </div>

            <div className="space-y-1">
              <div className="text-sm font-medium text-muted-foreground">
                Survey ID
              </div>
              <Badge
                variant={debriefDetails?.include_members ? "default" : "secondary"}
                className={cn(
                  "text-sm",
                  debriefDetails?.include_members
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-gray-600 hover:bg-gray-700"
                )}
              >
                {debriefDetails?.include_members ? "Sharable" : "Non-sharable"}
              </Badge>
              <div className="text-base font-mono text-sm mt-1">
                {debriefDetails?.survey_id}
              </div>
            </div>
          </div>

          <Separator />

          {/* Secondary Information */}
          <div className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground">Secondary</div>
            <div className="flex flex-wrap gap-2">
              {debriefDetails?.secondary ? (
                debriefDetails.secondary.map((s) => (
                  <Badge key={s} variant="outline">
                    {s}
                  </Badge>
                ))
              ) : (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  All Secondary included
                </Badge>
              )}
            </div>
          </div>

          <Separator />

          {/* Dates and Statistics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-1">
              <div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Survey ID
              </div>
              <div className="text-base font-mono">{debriefDetails?.id}</div>
            </div>
            
            <div className="space-y-1">
              <div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Created Date
              </div>
              <div className="text-base">
                {debriefDetails?.created_at &&
                  format(
                    new Date(debriefDetails?.created_at),
                    "dd-MMM-yyyy hh:mm"
                  )}
              </div>
            </div>
            
            <div className="space-y-1">
              <div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Scheduled Date
              </div>
              <div className="text-base">
                {debriefDetails?.scheduled_for &&
                  format(
                    new Date(debriefDetails?.scheduled_for),
                    "dd-MMM-yyyy hh:mm"
                  )}
              </div>
            </div>
            
            <div className="space-y-1">
              <div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Close Date
              </div>
              <div className="text-base">
                {debriefDetails?.close_at &&
                  format(
                    new Date(debriefDetails?.close_at),
                    "dd-MMM-yyyy hh:mm"
                  )}
              </div>
            </div>
            
            <div className="space-y-1">
              <div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Users className="h-4 w-4" />
                Sent To
              </div>
              <div className="text-base font-semibold">{debriefDetails?.total_requests}</div>
            </div>
            
            <div className="space-y-1">
              <div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Responded
              </div>
              <div className="text-base font-semibold text-green-600">
                {debriefDetails?.total_responses}
              </div>
            </div>
            
            <div className="space-y-1">
              <div className="text-sm font-medium text-muted-foreground">Name UID</div>
              <div className="text-base font-mono text-sm">{debriefDetails?.name_uid}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Additional Type Section */}
      <div className="space-y-4">
        <AdditionalType
          data={debriefDetails?.additional_type}
          debriefDetailsId={debriefDetails?.id}
        />
      </div>

      {/* MemberList with search and pagination */}
      {debriefDetails?.id && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <h2 className="text-2xl font-semibold tracking-tight">Survey Members</h2>
          </div>
          <MemberList debriefScheduleID={debriefDetails.id} />
        </div>
      )}

      {/* Members Status Section */}
      {!searchTerm && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Members Included and Completed Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {debriefDetails?.request_list?.length !==
              debriefDetails?.total_requests && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {debriefDetails?.request_list?.length - debriefDetails?.total_requests}{" "}
                  Members had their survey cancelled
                </AlertDescription>
              </Alert>
            )}
            <EmpList
              rows={debriefDetails?.request_list}
              completed={debriefDetails?.compliance}
              totalRequests={debriefDetails?.total_requests}
            />
          </CardContent>
        </Card>
      )}

      {/* AI Summary Section */}
      {!searchTerm && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>AI Summary</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAiToggle}
                className="gap-2"
              >
                {isAiHidden ? (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    Show
                  </>
                ) : (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    Hide
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          {!isAiHidden && (
            <CardContent>
              <ErrorWrapperCustom>
                <AiSummary getchat_gpt={getchat_gpt} />
              </ErrorWrapperCustom>
            </CardContent>
          )}
        </Card>
      )}

      {/* Tokens Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          <h2 className="text-2xl font-semibold tracking-tight">Tokens</h2>
        </div>
        <TokenList deBriefId={deBriefId} searchTerm={searchTerm} />
      </div>

      {/* Responses Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-primary" />
          <h2 className="text-2xl font-semibold tracking-tight">Responses</h2>
        </div>
        <ResponseList
          deBriefId={deBriefId}
          empUpdateRow={empUpdateRow}
          setEmpUpdateRow={setEmpUpdateRow}
          searchTerm={searchTerm}
        />
      </div>

      {/* Edit Schedule Dialog */}
      {debriefDetails?.id && (
        <EditScheduleDialog
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          debriefDetails={debriefDetails}
        />
      )}
    </div>
  );
};

export default Details;
